import { HttpErrorResponse } from '@angular/common/http';
import { Component, Inject, OnDestroy } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { translate } from '@jsverse/transloco';
import { Subject } from 'rxjs';
import { DropdownObject } from '@inmoment/the-kitchen';

import { Scale } from 'app/components/uikit/lex-horizontal-stepper/lex-horizontal-stepper.component';
import { GroupService } from 'app/providers/semantria-api/group.service';
import { StorageAccountService } from 'app/providers/storage-api/blender-account.service';
import { FieldService } from 'app/providers/storage-api/field.service';
import { AlertService } from 'app/services/alert.service';
import { EnableClassTelemetry } from 'app/services/otel.service';
import { TimezoneService } from 'app/services/timezone.service';
import { PredictiveDateRange } from '../predictive-widget-datepickers/predictive-widget-datepickers.component';
import { Breakdown, Driver } from '../../models/predictive.models';
import {
  AnalyticItemImpact,
  SmartSummaryDocumentConfig,
  SmartSummaryService,
  SmartSummaryStyle,
} from '../../services/smart-summary.service';

@EnableClassTelemetry()
@Component({
  selector: 'app-smart-summary-dialog',
  templateUrl: './smart-summary-dialog.component.html',
  styleUrls: ['./smart-summary-dialog.component.scss'],
  standalone: false,
})
export class SmartSummaryDialogComponent implements OnDestroy {
  length = 1;
  lengthScale: Scale = {
    min: 1,
    max: 8,
    step: 1,
  };

  defaultLanguage = { title: translate('widgets.smart-summary.english'), id: 'English' };
  readonly languageOptions: DropdownObject[] = [
    this.defaultLanguage,
    { title: translate('widgets.smart-summary.french'), id: 'French' },
    { title: translate('widgets.smart-summary.german'), id: 'German' },
    { title: translate('widgets.smart-summary.italian'), id: 'Italian' },
    { title: translate('widgets.smart-summary.spanish'), id: 'Spanish' },
  ];

  styleRadios = [
    {
      label: translate('widgets.smart-summary.data-analyst'),
      value: SmartSummaryStyle.DATA_ANALYST,
    },
    {
      label: translate('widgets.smart-summary.statistical'),
      value: SmartSummaryStyle.STATISTICAL,
    },
  ];

  formGroup: FormGroup;
  taskCreationInProgress = false;
  taskCreated = false;

  private onDestroy$ = new Subject<void>();

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: {
      projectId: string;
      collectionId: string;
      analysisId: string;
      driverAnalysisId: string;
      breakdownColumn: string;
      breakdown: Breakdown;
      driver: Driver;
      dateRange: PredictiveDateRange;
      hasAnalyticItem: boolean;
      analyticItemType?: string;
      analyticItemName?: string;
      analyticItemImpact?: AnalyticItemImpact;
      documentQuery?: string;
    },
    private matDialog: MatDialogRef<SmartSummaryDialogComponent>,
    private groupService: GroupService,
    private fb: FormBuilder,
    private accountService: StorageAccountService,
    private alertService: AlertService,
    private smartSummaryService: SmartSummaryService,
    private timezoneService: TimezoneService
  ) {
    this.createFormGroup();

    if (this.data.breakdownColumn) {
      this.accountService.groupFieldsSub.latest().then(() => {
        const breakdownFieldFormValue = `${FieldService.getFieldLongName(
          this.data.breakdownColumn
        )} - ${this.data.breakdown.name}`;
        this.formGroup.controls.breakdownField.setValue(breakdownFieldFormValue);
      });
    } else {
      this.formGroup.controls.breakdownField.setValue(this.data.breakdown.name);
    }
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  private createFormGroup(): void {
    this.formGroup = this.fb.group({
      breakdownField: [
        {
          value: '',
          disabled: true,
        },
        Validators.required,
      ],
      driver: [{ value: this.data.driver.fieldLongName, disabled: true }, Validators.required],
      analyticItemName: [{ value: this.data.analyticItemName, disabled: true }],
      target: this.fb.group(
        {
          like: [true],
          dislike: [false],
          improve: [false],
        },
        {
          validators: this.getTargetValidator(),
        }
      ),
      style: [SmartSummaryStyle.DATA_ANALYST, Validators.required],
      language: [this.defaultLanguage.id, Validators.required],
    });
  }

  private getTargetValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (Object.values((control as FormGroup).controls).every((ctrl) => !ctrl.value)) {
        return {
          target: 'required',
        };
      }

      return null;
    };
  }

  private getTaskPayload(): SmartSummaryDocumentConfig {
    const formValues = this.formGroup.value;

    const config: SmartSummaryDocumentConfig = {
      source: 'IP Dashboard',
      groupId: this.groupService.getSelectedGroupId(true),
      projectId: this.data.projectId,
      collectionId: this.data.collectionId,
      analysisId: this.data.analysisId,
      driverAnalysisId: this.data.driverAnalysisId,
      breakdownFieldValue: this.data.breakdown.name,
      driverFieldName: this.data.driver.name,
      analyticItem: this.data.hasAnalyticItem,
      documentQuery: this.data.documentQuery,
      timezone: this.timezoneService.timezone,
      previousStart: this.data.dateRange?.previous.first,
      previousEnd: this.data.dateRange?.previous.last,
      currentStart: this.data.dateRange?.current.first,
      currentEnd: this.data.dateRange?.current.last,
      scenarioParameters: {
        length: this.length,
        style: formValues.style,
        language: formValues.language,
        analyticItemType: this.data.analyticItemType,
        analyticItemName: this.data.analyticItemName,
        analyticItemImpact: this.data.analyticItemImpact,
      },
    };

    return config;
  }

  onSummarize(): void {
    Object.values(this.formGroup.controls).forEach((ctrl) => ctrl.updateValueAndValidity());

    if (this.formGroup.valid) {
      this.taskCreationInProgress = true;
      this.smartSummaryService
        .createSummary(
          this.getTaskPayload(),
          () =>
            (this.data.hasAnalyticItem
              ? this.data.analyticItemName
              : this.data.driver.fieldLongName) + ' - Smart Summary.docx'
        )
        .then(this.onSummarizationSuccess.bind(this), this.onSummarizationError.bind(this))
        .finally(() => (this.taskCreationInProgress = false));
    }
  }

  private onSummarizationSuccess(): void {
    this.taskCreated = true;
  }

  private onSummarizationError(error: HttpErrorResponse | Error): void {
    this.alertService.showErrorToast(translate('widgets.smart-summary.unable-to-summarize'), error);
  }

  onClose(): void {
    this.matDialog.close();
  }

  onOpenDownloadCenter(element: HTMLElement): void {
    element.dispatchEvent(new Event('open-download-center', { bubbles: true }));
    this.matDialog.close();
  }
}
